import type { GameFlow } from "./game-flow";
import {
  parseChallengeLink,
  type ShareableChallengeIdentity,
} from "../config/challenge-link-config";
import type { DailyChallengeIdentity } from "../config/daily-challenge-config";
import type { SeasonalChallengeIdentity } from "../config/challenge-link-config";

export type ChallengeLinkRouteResult =
  "daily" | "home" | "ignored" | "seasonal";

export interface ChallengeLinkStartPort {
  startDaily(identity: DailyChallengeIdentity): boolean;
  startSeasonal(identity: SeasonalChallengeIdentity): boolean;
}

export interface NativeChallengeLinkListenerHandle {
  remove(): Promise<void>;
}

export interface NativeChallengeLinkSource {
  addListener(
    eventName: "appUrlOpen",
    listener: (event: { readonly url: string }) => void,
  ): Promise<NativeChallengeLinkListenerHandle>;
  getLaunchUrl(): Promise<{ readonly url: string } | undefined>;
}

export class ChallengeLinkRouter {
  public constructor(
    private readonly flow: GameFlow,
    private readonly starts: ChallengeLinkStartPort,
  ) {}

  public route(value: string): ChallengeLinkRouteResult {
    if (this.hasNoChallenge(value)) {
      return "ignored";
    }
    const target = parseChallengeLink(value);
    if (target === null || !this.start(target.identity, target.kind)) {
      this.flow.showHome();
      return "home";
    }
    return target.kind;
  }

  private hasNoChallenge(value: string): boolean {
    try {
      return !new URL(value).searchParams.has("challenge");
    } catch {
      return false;
    }
  }

  private start(
    identity: ShareableChallengeIdentity,
    kind: "daily" | "seasonal",
  ): boolean {
    return kind === "daily"
      ? this.starts.startDaily(identity as DailyChallengeIdentity)
      : this.starts.startSeasonal(identity as SeasonalChallengeIdentity);
  }
}

export class NativeChallengeLinkCoordinator {
  private destroyed = false;
  private listenerHandle: NativeChallengeLinkListenerHandle | null = null;
  private startPromise: Promise<boolean> | null = null;

  public constructor(
    private readonly router: Pick<ChallengeLinkRouter, "route">,
    private readonly source: NativeChallengeLinkSource,
  ) {}

  public destroy(): Promise<void> {
    if (this.destroyed) {
      return Promise.resolve();
    }
    this.destroyed = true;
    return this.removeListener();
  }

  public start(): Promise<boolean> {
    if (this.destroyed) {
      return Promise.resolve(false);
    }
    this.startPromise ??= this.install();
    return this.startPromise;
  }

  private readonly handleUrl = (event: { readonly url: string }): void => {
    if (!this.destroyed) {
      this.router.route(event.url);
    }
  };

  private async install(): Promise<boolean> {
    let handle: NativeChallengeLinkListenerHandle;
    try {
      handle = await this.source.addListener("appUrlOpen", this.handleUrl);
    } catch {
      return false;
    }
    if (this.destroyed) {
      await handle.remove().catch(() => undefined);
      return false;
    }
    this.listenerHandle = handle;
    try {
      const launch = await this.source.getLaunchUrl();
      if (launch !== undefined) {
        this.handleUrl(launch);
      }
    } catch {
      return true;
    }
    return !this.destroyed;
  }

  private async removeListener(): Promise<void> {
    if (this.listenerHandle === null && this.startPromise !== null) {
      await this.startPromise;
    }
    const handle = this.listenerHandle;
    this.listenerHandle = null;
    if (handle !== null) {
      await handle.remove().catch(() => undefined);
    }
  }
}

export const startChallengeLinkHandling = (
  native: boolean,
  router: Pick<ChallengeLinkRouter, "route">,
  source: NativeChallengeLinkSource,
  href: string,
): NativeChallengeLinkCoordinator | null => {
  if (!native) {
    router.route(href);
    return null;
  }
  const coordinator = new NativeChallengeLinkCoordinator(router, source);
  void coordinator.start();
  return coordinator;
};
